import React from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AppShell from "../AppShell";
import ImageWithFallback from "../ImageWithFallback";
import usePageTitle from "../../hooks/usePageTitle";

const setCategories = [
  { title: "Pokémon Sets", detail: "Browse modern, vintage, promotional, and special Pokémon releases.", path: "/sets/pokemon", image: "/sets/pokemon sets.webp" },
  { title: "One Piece Sets", detail: "Explore One Piece booster, starter, extra booster, and promotional releases.", path: "/sets/one-piece", image: "/sets/one piece sets.webp" },
  { title: "Football Card Sets", detail: "Browse football card releases and the collectibles grouped inside each set.", path: "/sets/football", image: "/sets/football sets.jpg" },
  { title: "Hockey Card Sets", detail: "Explore hockey releases, flagship sets, rookies, inserts, and parallels.", path: "/sets/hockey", image: "/sets/hockey sets.avif" },
  { title: "Basketball Card Sets", detail: "Browse basketball card releases and available set inventory.", path: "/sets/basketball", image: "/sets/basketball sets.webp" },
  { title: "Soccer Card Sets", detail: "Explore soccer releases, international products, club sets, and World Cup cards.", path: "/sets/soccer", image: "/sets/soccer sets.jpg" },
];

const cardSx = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: 3,
  border: "1px solid #dce7f3",
  bgcolor: "#fff",
  color: "inherit",
  textDecoration: "none",
  boxShadow: "0 10px 26px rgba(15,39,71,.06)",
  transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
  "&:hover": { transform: "translateY(-3px)", boxShadow: "0 16px 34px rgba(15,39,71,.12)", borderColor: "#91b9e4" },
  "&:focus-visible": { outline: "3px solid rgba(25,118,210,.35)", outlineOffset: 3 },
};

export default function AllOtherSetsPage() {
  usePageTitle("Browse Sets");
  return (
    <AppShell>
      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2.5, md: 3.5 }, py: { xs: 1, md: 2 }, minWidth: 0 }}>
        <Paper component="header" elevation={0} sx={{ p: { xs: 2.5, sm: 3.5, md: 4.5 }, borderRadius: { xs: 3, md: 4 }, border: "1px solid #d8e6f4", background: "linear-gradient(145deg, #f7fbff 0%, #edf5fd 100%)", boxShadow: "0 16px 38px rgba(15,39,71,.08)" }}>
          <Typography variant="overline" sx={{ color: "#235fa4", fontWeight: 800, letterSpacing: 1.2 }}>Explore the database</Typography>
          <Typography component="h1" variant="h3" sx={{ color: "#10233f", fontWeight: 900, mt: .25, fontSize: { xs: "2rem", md: "3rem" } }}>Browse Collectible Sets</Typography>
          <Typography sx={{ color: "#42566f", mt: 1.25, maxWidth: 820, lineHeight: 1.65 }}>Explore Pokémon, One Piece, football, hockey, basketball, and soccer sets from one place.</Typography>
          <Typography variant="body2" sx={{ color: "#52677f", mt: 1, fontWeight: 600 }}>Choose a category to browse available releases and the collectibles inside them.</Typography>
        </Paper>

        <Grid container spacing={{ xs: 2, md: 2.5 }}>
          {setCategories.map((category) => (
            <Grid item xs={12} sm={6} lg={4} key={category.path}>
              <Paper component={RouterLink} to={category.path} elevation={0} sx={cardSx}>
                <Box sx={{ aspectRatio: "16 / 9", bgcolor: "#eaf1f8", overflow: "hidden" }}>
                  <ImageWithFallback src={category.image} alt={category.title} placeholderSrc="/logos/default-item.jpg" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </Box>
                <Box sx={{ p: { xs: 2, md: 2.25 }, display: "flex", flexDirection: "column", flexGrow: 1 }}>
                  <Typography component="h2" variant="h6" sx={{ color: "#10233f", fontWeight: 850 }}>{category.title}</Typography>
                  <Typography variant="body2" sx={{ color: "#52677f", lineHeight: 1.6, mt: .75, mb: 2 }}>{category.detail}</Typography>
                  <Typography sx={{ color: "#235fa4", fontWeight: 800, mt: "auto" }}>Browse sets →</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper component="section" variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, borderColor: "#dce7f3", bgcolor: "#fff", display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography component="h2" variant="h5" sx={{ color: "#10233f", fontWeight: 850 }}>Not sure where to start?</Typography>
            <Typography sx={{ color: "#52677f", mt: .5 }}>Browse the full collectible database to search across every supported category.</Typography>
          </Box>
          <Button component={RouterLink} to="/search?q=__all__" variant="contained" sx={{ minHeight: 44, px: 2.5, flexShrink: 0 }}>Explore the Database</Button>
        </Paper>
      </Box>
    </AppShell>
  );
}
